import useTableShortcuts from "@/components/shared/tables/tableShortCuts";
import { useInvoicesContext } from "@/providers/invoices";
import type { UseGetAllInvoicesData } from "@/queries/invoices/get-all";
import {
  type ColumnFilter,
  type OnChangeFn,
  type SortingState,
  type Table,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";

import type { InvoiceRowData } from "@/components/shared/data-table";
import type { ServerFilters } from "@/hooks/useQueryFilters";
import type { InvoiceTypeString } from "@/lib/constants/invoice-type";
import { ServerSideFilteringFeature } from "@/lib/table/server-side-filtering";
import type { InvoiceFilterFields } from "@/types/utils";
import { columns as _columns } from "./columns";

const InvoiceTableInstanceContext = createContext<
  | {
      table: Table<any>;
      tableName: string;
      clickableColumns: string[];
      setRowSelection: (value: any) => void;
    }
  | undefined
>(undefined);

export function useInvoiceTableInstanceContext() {
  const context = useContext(InvoiceTableInstanceContext);
  if (context === undefined) {
    throw new Error(
      "useInvoiceTableContext must be within InvoiceTableInstanceProvider",
    );
  }

  return context;
}

const _columnVisibility = {
  type: false,
  invoiceNo: true,
  invoiceDate: true,
  startDate: true,
  expenseAccount: true,
  datevBu: true,
  tags: true,
  createdAt: true,
  status: true,
  reviewType: false,
  ocrReviewed: false,
  isBooking: false,
  isDuplicate: false,
};

type Props = PropsWithChildren<{
  tableName: string;
  data: UseGetAllInvoicesData;
  initialColumnFilters?: ColumnFilter[];
  showConfidences?: boolean;
  clickableColumns?: string[];
  columnVisibility?: Record<string, boolean>;
  totalItems: number;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  columnFilters: ColumnFilter[];
  readOnly?: boolean;
  invoiceType?: InvoiceTypeString;
  initialServerSideFilters: ServerFilters<InvoiceFilterFields>;
  globalServerSideSearch: string;
  serverSideFilters: ServerFilters<InvoiceFilterFields>;
  onServerSideFiltersClear: () => void;
}>;

export const InvoiceTableInstanceProvider = ({
  data,
  children,
  tableName,
  clickableColumns = [
    "supplierName",
    "totalAmount",
    "description",
    "invoiceNo",
    "invoiceDate",
    "serviceDate",
    "createdAt",
  ],
  columnVisibility = _columnVisibility,
  showConfidences = false,
  totalItems,
  sorting,
  onSortingChange,
  columnFilters,
  readOnly = false,
  invoiceType,
  initialServerSideFilters,
  globalServerSideSearch,
  serverSideFilters,
  onServerSideFiltersClear,
}: Props) => {
  const { taxCodes, accounts } = useInvoicesContext();

  const [rowSelection, setRowSelection] = useState({});

  const columns = useMemo(
    () =>
      _columns(
        showConfidences,
        taxCodes ?? [],
        accounts ?? [],
        readOnly,
        invoiceType,
      ),
    [showConfidences, taxCodes, accounts, readOnly, invoiceType],
  );

  const invoices = data?.map((invoice) => invoice.data) ?? [];

  const table = useReactTable<InvoiceRowData>({
    data: invoices,
    columns,
    getRowId: (row) => row.invoiceId,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange,
    filterFns: {},
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: () => {},
    enableColumnResizing: true,
    enableMultiRowSelection: true,
    autoResetPageIndex: false,
    manualPagination: true,
    manualSorting: true,
    rowCount: totalItems,
    initialServerSideFilters,
    onServerSideFiltersClear,
    state: {
      rowSelection,
      sorting,
      columnFilters,
      globalServerSideSearch,
      serverSideFilters,
    },
    initialState: {
      columnVisibility,
    },
    _features: [ServerSideFilteringFeature],
  });

  useTableShortcuts({
    handleCmdA: () => table.toggleAllRowsSelected(true),
    handleEsc: () => table.resetRowSelection(true),
  });

  const context = useMemo(
    () => ({
      table,
      tableName,
      setRowSelection,
      clickableColumns,
    }),
    [table, tableName, clickableColumns],
  );

  return (
    <InvoiceTableInstanceContext.Provider value={context}>
      {children}
    </InvoiceTableInstanceContext.Provider>
  );
};
